import { apiClient } from '../../../services/api/api-client';

function extractMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

const ACTION_CODE_MAP = {
  CREATE_CONTENT: 'create',
  DELETE_CONTENT: 'delete',
  EDIT_CONTENT: 'edit',
  PUBLISH_CONTENT: 'publish',
  VIEW_CONTENT: 'view',
};

const ALLOWED_ROLE_CODES = ['Content_Creator', 'Manager CMS', 'ADMIN', 'SUPER_ADMIN'];

function extractCmsPermissions(user) {
  const perms = new Set();
  const modules = Array.isArray(user?.PermissionsByModule) ? user.PermissionsByModule : [];
  const cmsModule = modules.find((m) => m?.Module === 'CMS');
  const actions = Array.isArray(cmsModule?.Actions) ? cmsModule.Actions : [];
  actions.forEach((a) => {
    const mapped = ACTION_CODE_MAP[a?.ActionCode];
    if (mapped) perms.add(mapped);
  });
  return Array.from(perms);
}

function computePermissions(roleCodes, cmsPermissions) {
  const perms = new Set(cmsPermissions);
  // If user has any allowed CMS role but API actions are missing, fall back to role-based mapping
  const hasAllowedRole = roleCodes.some((code) => ALLOWED_ROLE_CODES.includes(code));
  if (hasAllowedRole && perms.size === 0) {
    const ROLE_FALLBACK = {
      Content_Creator: ['create', 'delete', 'view'],
      'Manager CMS': ['create', 'view', 'edit', 'delete', 'publish'],
      ADMIN: ['create', 'view', 'edit', 'delete', 'publish'],
      SUPER_ADMIN: ['create', 'view', 'edit', 'delete', 'publish'],
    };
    roleCodes.forEach((code) => {
      const list = ROLE_FALLBACK[code];
      if (list) list.forEach((p) => perms.add(p));
    });
  }
  return Array.from(perms);
}

export const authService = {
  login: async ({ username, password }) => {
    try {
      const res = await apiClient.post('/Staff/auth/login', { username, password });
      const data = res?.data ?? res;
      const payload = data?.data ?? data;
      if (!payload?.access_token) {
        const err = new Error(data?.message || 'Invalid username or password.');
        err.code = 'INVALID_CREDENTIALS';
        throw err;
      }
      const user = payload?.user ?? payload;
      const roles = Array.isArray(user?.Roles) ? user.Roles : [];
      const roleCodes = roles.map((r) => r.RoleCode).filter(Boolean);

      const modules = Array.isArray(user?.PermissionsByModule) ? user.PermissionsByModule : [];
      const hasCmsModule = modules.some((m) => m?.Module === 'CMS');
      if (!hasCmsModule) {
        const err = new Error('You are not authorized to access the CMS. Please contact your administrator.');
        err.code = 'UNAUTHORIZED_MODULE';
        throw err;
      }

      const hasAllowedRole = roleCodes.some((code) => ALLOWED_ROLE_CODES.includes(code));
      if (!hasAllowedRole) {
        const err = new Error('You do not have the required role to access the CMS.');
        err.code = 'UNAUTHORIZED_ROLE';
        throw err;
      }

      const cmsPermissions = extractCmsPermissions(user);
      const permissions = computePermissions(roleCodes, cmsPermissions);

      return {
        user: payload.user,
        accessToken: payload.access_token,
        refreshToken: payload.refresh_token,
        expiresIn: payload.expires_in,
        tokenType: payload.token_type,
        permissions,
        roles: roleCodes,
      };
    } catch (error) {
      if (error.code === 'INVALID_CREDENTIALS' || error.code === 'UNAUTHORIZED_MODULE' || error.code === 'UNAUTHORIZED_ROLE') {
        throw error;
      }
      const err = new Error(extractMessage(error, 'Invalid username or password.'));
      err.code = error?.response?.status === 401 ? 'INVALID_CREDENTIALS' : 'LOGIN_ERROR';
      throw err;
    }
  },

  logout: async (refreshToken) => {
    try {
      await apiClient.post('/auth/logout', { refresh_token: refreshToken });
    } catch {
      // ignore logout API errors; client-side cleanup happens regardless
    }
    return { success: true };
  },

  refresh: async (refreshToken) => {
    const res = await apiClient.post('/staff/auth/refresh', { refresh_token: refreshToken });
    const data = res?.data ?? res;
    return {
      accessToken: data?.access_token,
      refreshToken: data?.refresh_token,
      expiresIn: data?.expires_in,
    };
  },

  changePassword: async ({ username, oldPassword, newPassword }) => {
    try {
      const res = await apiClient.post('/auth/change-password', {
        Username: username,
        OldPassword: oldPassword,
        NewPassword: newPassword,
      });
      return res;
    } catch (error) {
      const err = new Error(extractMessage(error, 'Failed to change password.'));
      throw err;
    }
  },

  forgotPassword: async (email) => {
    const res = await apiClient.post('/auth/forgot-password', { email });
    return res;
  },
};

